import { useEffect, useRef, useState } from 'react'
import ProfilePicker from '@/frontend/features/AgentProfiles/ProfilePicker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import NameShuffleButton from '@/frontend/components/DigitalWorkers/NameShuffleButton'
import HarnessSelector from '@/frontend/pages/Dashboard/DigitalWorkers/new/HarnessSelector'
import {
  CODEX_TEMPLATE_IDS,
  SELECTABLE_PROFILE_IDS,
} from '@/frontend/pages/Dashboard/DigitalWorkers/constants'
import type { ProfileStepProps } from '@/frontend/pages/Dashboard/DigitalWorkers/new/types'

export default function ProfileStep({
  data,
  errors,
  disabled = false,
  fleetProfiles,
  profileOptions = [], profileCopy = {},
  onFieldChange,
  onShuffleName,
  showEmailProvisioningToggle = false,
  showHarnessSelector = false,
  showCodexTemplates = false,
  agentHarnesses = ['openclaw'],
}: ProfileStepProps) {
  const selectedProfile = data.fleet_profile
  const availableProfileIds: readonly string[] = data.agent_harness === 'codex' ? CODEX_TEMPLATE_IDS : SELECTABLE_PROFILE_IDS
  const selectableProfiles = fleetProfiles.filter((profile) => availableProfileIds.includes(profile))
  const [nameFlash, setNameFlash] = useState(false)
  const flashTimeoutRef = useRef<number | null>(null)

  useEffect(() => () => {
    if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current)
  }, [])

  const handleShuffleTrigger = () => {
    onShuffleName()
    setNameFlash(true)
    if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current)
    flashTimeoutRef.current = window.setTimeout(() => setNameFlash(false), 1200)
  }

  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <h2 className="text-2xl font-semibold tracking-tight text-foreground">Crie seu novo colaborador digital</h2>
        <p className="text-sm text-muted-foreground">Comece definindo o nome e cargo do seu agente.</p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="worker-name">Nome</Label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            id="worker-name"
            value={data.name}
            onChange={(event) => onFieldChange('name', event.target.value)}
            placeholder="Ex: Tobi"
            disabled={disabled}
            className={cn(
              'sm:flex-1 transition-all duration-300',
              nameFlash && 'ring-2 ring-violet-500/50 border-violet-400 shadow-[0_0_0_4px_rgba(139,92,246,0.1)]',
            )}
          />
          <NameShuffleButton
            currentName={data.name}
            onPick={(name) => onFieldChange('name', name)}
            onTrigger={handleShuffleTrigger}
            disabled={disabled}
          />
        </div>
        {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
        {(errors.stationzero_email || errors.instance_name) && (
          <p className="text-sm text-destructive">Use um nome com letras e números para continuar.</p>
        )}
      </div>

      {showEmailProvisioningToggle && (
        <div className="flex items-center justify-between gap-4 border-y border-border/70 py-3">
          <Label htmlFor="reuse-stationzero-email" className="text-sm font-medium">
            Não provisionar email
          </Label>
          <Switch
            id="reuse-stationzero-email"
            checked={data.reuse_stationzero_email}
            onCheckedChange={(checked) => onFieldChange('reuse_stationzero_email', Boolean(checked))}
            disabled={disabled}
          />
        </div>
      )}

      {showHarnessSelector && (
        <HarnessSelector
          value={data.agent_harness}
          available={agentHarnesses}
          disabled={disabled}
          onChange={(value) => onFieldChange('agent_harness', value)}
        />
      )}

      {(data.agent_harness !== 'codex' || showCodexTemplates || profileOptions.length > 0) && (
        <div className="grid gap-2">
          <ProfilePicker builtIns={selectableProfiles} profiles={data.agent_harness === 'codex' ? profileOptions : []}
            copy={profileCopy} selected={selectedProfile} disabled={disabled} onSelect={key => onFieldChange('fleet_profile', key)} />
          {errors.fleet_profile && <p className="text-sm text-destructive">{errors.fleet_profile}</p>}
        </div>
      )}
    </div>
  )
}
